import type { User } from "firebase/auth";
import React, { useEffect, useMemo, useState } from "react";

import type { AuthContext, AuthenticatedResult, AuthInitializeOptions } from "js/controllers/AuthController";
import getLogger, { LogGroup } from "js/core/logger";
import { RemoveSplashScreen } from "js/core/SplashScreen";
import { FirebaseAuthInitalizer } from "js/react/views/Auth/FirebaseAuthInitalizer";
import { FirebaseSessionTracker } from "js/react/views/Auth/FirebaseSessionTracker";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";

const logger = getLogger(LogGroup.AUTH);

type AuthGateStatus = "auth" | "ready";

interface AuthGateProps {
    defaultPage: "signIn" | "createAccount";
    readonlyEmail?: string;
    context?: AuthContext;
    children: React.ReactNode;
}

type FirebaseUser = User & { signedUpInCurrentSession?: boolean };

async function loadOnboardingPage(): Promise<React.ComponentType<{ authOptions: AuthInitializeOptions }>> {
    // @ts-ignore
    const { default: OnboardingPage } = await import(/* webpackChunkName: "onboarding" */ "js/react/views/Onboarding/Onboarding");
    return OnboardingPage;
}

const AuthGateImplementation = withFirebaseUser(function AuthGateImplementation({ firebaseUser, defaultPage, readonlyEmail, context, children }: AuthGateProps & { firebaseUser: FirebaseUser | null }) {
    const isSignedIn = !!firebaseUser;

    // Only verified users who were already signed in when the gate mounted are let in based on the
    // firebase user. Users signing in through the auth ui are reported by firebase before they're
    // registered, so they're let in by the AuthController instead, and so are unverified users.
    const [signedInStatus, setStatus] = useState<AuthGateStatus>(() => firebaseUser?.emailVerified ? "ready" : "auth");
    // Logging out (e.g. from another tab) takes the firebase user away before any effect runs,
    // the app must never be rendered without it
    const status = isSignedIn ? signedInStatus : "auth";
    const [OnboardingPage, setOnboardingPage] = useState<React.ComponentType<{ authOptions: AuthInitializeOptions }> | null>(null);

    const authOptions = useMemo<AuthInitializeOptions>(() => ({
        mode: "embedded",
        defaultPageMode: defaultPage,
        readonlyEmail,
        context,
        onAuthenticated: ({ user, isNewUser }: AuthenticatedResult) => {
            // Mount the flag on the current user so downstream components can know if the user
            // was registered in the current session
            (user as FirebaseUser).signedUpInCurrentSession = isNewUser;
            setStatus("ready");
        }
    }), []);

    useEffect(() => {
        if (!isSignedIn) {
            // Logged out
            setStatus("auth");
        }
    }, [isSignedIn]);

    useEffect(() => {
        if (status !== "auth" || OnboardingPage) {
            return;
        }

        let isMounted = true;
        loadOnboardingPage()
            .then(LoadedOnboardingPage => {
                if (isMounted) {
                    setOnboardingPage(() => LoadedOnboardingPage);
                }
            })
            .catch(err => logger.error(err, "[AuthGate] failed to load the onboarding page"));

        return () => {
            isMounted = false;
        };
    }, [status]);

    useEffect(() => {
        if (status === "ready" || (status === "auth" && OnboardingPage)) {
            RemoveSplashScreen();
        }
    }, [status, OnboardingPage]);

    if (status === "ready") {
        return (
            <FirebaseSessionTracker user={firebaseUser}>
                {children}
            </FirebaseSessionTracker>
        );
    }

    if (status === "auth" && OnboardingPage) {
        return <OnboardingPage authOptions={authOptions} />;
    }

    return null;
}) as React.ForwardRefExoticComponent<AuthGateProps>;

export function AuthGate(props: AuthGateProps) {
    return (
        <FirebaseAuthInitalizer>
            <AuthGateImplementation {...props} />
        </FirebaseAuthInitalizer>
    );
}
